import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Save } from "lucide-react";

const UFS = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG",
  "PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

const ROLES = [
  { k: "CLAIMANT",       l: "Reclamante (autor)",         d: "Cálculo pró-reclamante." },
  { k: "DEFENDANT",      l: "Reclamada (ré/empresa)",     d: "Auditoria contra excessos." },
  { k: "NEUTRAL_EXPERT", l: "Perito / neutro",            d: "Não favorecer nenhum polo." },
];

export default function LiquidacaoNova() {
  const nav = useNavigate();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    process_number: "",
    claimant: { name: "", cpf: "" },
    defendant: { name: "", cnpj: "" },
    court: "",
    regional_court: "",
    city: "",
    uf: "",
    filing_date: "",
    employment_start: "",
    employment_end: "",
    calculation_start: "",
    calculation_end: "",
    calculation_role: "CLAIMANT",
    mode: "AUTO",
  });

  const submit = async () => {
    if (!form.process_number.trim()) {
      toast.error("Informe o número do processo (CNJ).");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/liq/cases", form);
      if (r.data._warning_duplicate_process) {
        toast.warning(r.data._warning_duplicate_process.message);
      }
      toast.success("Liquidação criada. Agora envie os documentos.");
      nav(`/calculos/liquidacao-trabalhista/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao criar liquidação");
    } finally {
      setSaving(false);
    }
  };

  const set = (path, val) => {
    setForm(prev => {
      const next = { ...prev };
      const keys = path.split(".");
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) {
        cur[keys[i]] = { ...cur[keys[i]] };
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = val;
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-6" data-testid="liq-new">
      <div className="max-w-3xl mx-auto">
        <Link to="/calculos/liquidacao-trabalhista"
          className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-3"
          data-testid="liq-new-back">
          <ArrowLeft className="w-3.5 h-3.5"/> Voltar
        </Link>
        <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500 mb-1">
          HERMES · Nova Liquidação
        </div>
        <h1 className="font-heading text-3xl font-semibold text-slate-900">
          Cadastro do processo
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Preencha os dados básicos. Todos os campos podem ser corrigidos depois —
          o Hermes salva automaticamente conforme você digita.
        </p>

        <div className="mt-6 space-y-5 bg-white rounded-2xl border border-slate-200 p-6">
          <Field label="Número do processo (CNJ)" required
                 value={form.process_number}
                 onChange={v => set("process_number", v)}
                 placeholder="0000000-00.0000.0.00.0000"
                 testid="liq-new-process" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Reclamante" value={form.claimant.name}
                   onChange={v => set("claimant.name", v)}
                   testid="liq-new-claimant" />
            <Field label="CPF do reclamante" value={form.claimant.cpf}
                   onChange={v => set("claimant.cpf", v)}
                   placeholder="000.000.000-00"
                   testid="liq-new-claimant-cpf" />
            <Field label="Reclamada" value={form.defendant.name}
                   onChange={v => set("defendant.name", v)}
                   testid="liq-new-defendant" />
            <Field label="CNPJ da reclamada" value={form.defendant.cnpj}
                   onChange={v => set("defendant.cnpj", v)}
                   placeholder="00.000.000/0000-00"
                   testid="liq-new-defendant-cnpj" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Vara" value={form.court}
                   onChange={v => set("court", v)}
                   placeholder="1ª Vara do Trabalho de Belo Horizonte"
                   testid="liq-new-court" />
            <Field label="TRT" value={form.regional_court}
                   onChange={v => set("regional_court", v)}
                   placeholder="TRT3"
                   testid="liq-new-regional" />
            <label className="block">
              <span className="text-xs font-medium text-slate-600 uppercase tracking-wide">UF</span>
              <select value={form.uf} onChange={e => set("uf", e.target.value)}
                className="mt-1 w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm"
                data-testid="liq-new-uf">
                <option value="">—</option>
                {UFS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </label>
          </div>

          <Field label="Cidade" value={form.city}
                 onChange={v => set("city", v)}
                 testid="liq-new-city" />

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <DateField label="Ajuizamento" value={form.filing_date}
                       onChange={v => set("filing_date", v)}
                       testid="liq-new-filing" />
            <DateField label="Admissão" value={form.employment_start}
                       onChange={v => set("employment_start", v)}
                       testid="liq-new-emp-start" />
            <DateField label="Rescisão" value={form.employment_end}
                       onChange={v => set("employment_end", v)}
                       testid="liq-new-emp-end" />
            <DateField label="Data da liquidação" value={form.calculation_end}
                       onChange={v => set("calculation_end", v)}
                       testid="liq-new-calc-end" />
          </div>

          <div>
            <div className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-2">
              Papel do calculista
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {ROLES.map(r => (
                <button key={r.k}
                  onClick={() => set("calculation_role", r.k)}
                  data-testid={`liq-new-role-${r.k}`}
                  className={`text-left rounded-xl border p-3 transition ${
                    form.calculation_role === r.k
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-200 bg-white hover:border-slate-400"}`}>
                  <div className="text-sm font-semibold">{r.l}</div>
                  <div className={`text-xs mt-0.5 ${
                    form.calculation_role === r.k ? "text-white/70" : "text-slate-500"}`}>
                    {r.d}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-3">
          <button onClick={() => nav("/calculos/liquidacao-trabalhista")}
            className="h-11 px-4 rounded-xl border border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-100"
            data-testid="liq-new-cancel">
            Cancelar
          </button>
          <button onClick={submit} disabled={saving}
            data-testid="liq-new-submit"
            className="h-11 px-5 rounded-xl bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white text-sm font-medium inline-flex items-center gap-2">
            {saving ? <Save className="w-4 h-4 animate-pulse"/> : <ArrowRight className="w-4 h-4"/>}
            {saving ? "Criando…" : "Criar e enviar documentos"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, required, placeholder, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-600 uppercase tracking-wide">
        {label}{required && <span className="text-rose-600"> *</span>}
      </span>
      <input value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm"
        data-testid={testid} />
    </label>
  );
}
function DateField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-600 uppercase tracking-wide">{label}</span>
      <input type="date" value={value} onChange={e => onChange(e.target.value)}
        className="mt-1 w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm"
        data-testid={testid} />
    </label>
  );
}
